import React, { useState } from 'react';
import {
  Box,
  Modal,
  IconButton,
  Paper,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  TextField as MuiTextField,
  InputAdornment,
  Typography
} from '@mui/material';
import Close from '@mui/icons-material/Close';
import ContentCopy from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { TextView, Button, TextField, Alert } from '@atoms';
import { BillingAccountSelector } from '@organisms';

import { callAddKey } from '@particles';

interface AddBillingAccountProps {
  open: boolean;
  onClose: () => void;
}

type KeyType = 'legacy' | 'client';

type Step = 'form' | 'success';

const CreateKeyModal: React.FC<AddBillingAccountProps> = ({ onClose, open }: AddBillingAccountProps) => {
  const [step, setStep] = useState<Step>('form');
  const [loading, setLoading] = useState<boolean>(false);
  const [title, setTitle] = useState<string>('');
  const [createdKey, setCreatedKey] = useState<string>('');
  const [keyRevealed, setKeyRevealed] = useState<boolean>(false);
  const [copied, setCopied] = useState<boolean>(false);
  const [keyType, setKeyType] = useState<KeyType>('legacy');
  const [selectedLocations, setSelectedLocations] = useState<BillingAccount[]>([]);
  const [expiresAt, setExpiresAt] = useState<string>('');

  const [error, setError] = useState<{ title: string; message: string } | undefined>();

  const resetForm = () => {
    setStep('form');
    setTitle('');
    setCreatedKey('');
    setKeyRevealed(false);
    setCopied(false);
    setKeyType('legacy');
    setSelectedLocations([]);
    setExpiresAt('');
    setError(undefined);
  };

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleCopyKey = () => {
    if (!createdKey) return;
    navigator.clipboard.writeText(createdKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateKey = () => {
    if (!title?.trim()) {
      setError({ title: 'Validation', message: 'Title is required' });
      return;
    }
    if (keyType === 'client' && selectedLocations.length === 0) {
      setError({ title: 'Validation', message: 'Please select at least one location for the client key' });
      return;
    }

    setLoading(true);
    setError(undefined);
    const payload: Parameters<typeof callAddKey>[0] = { name: title.trim() };
    if (keyType === 'client') {
      // Always include `locationIds` for client keys — server rejects an empty
      // list, acting as a second-line defence if the form-level check is missed.
      payload.locationIds = selectedLocations
        .map((l) => l.locationId)
        .filter((id): id is string => !!id);
      if (expiresAt) payload.expiresAt = new Date(expiresAt).toISOString();
    }

    callAddKey(payload)
      .then((res) => {
        if (res.code === 200 && res.data?.data?.key) {
          setCreatedKey(res.data.data.key);
          setStep('success');
        } else {
          setError({
            title: 'Error',
            message: (res as any).error?.message ?? 'Failed to create key'
          });
        }
        setLoading(false);
      })
      .catch((err) => {
        setError({ title: 'Error', message: err?.message ?? String(err) });
        setLoading(false);
      });
  };

  const renderFormStep = () => (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Create a key for webhooks (legacy) or for client access to Core Contacts API (location-scoped, optional expiry).
      </Typography>

      <TextField
        label="Key name"
        name="name"
        fullWidth
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="e.g. Production webhook, Client XYZ"
        sx={{ mb: 2 }}
      />

      <FormControl component="fieldset" sx={{ mb: 2 }}>
        <FormLabel component="legend">Key type</FormLabel>
        <RadioGroup
          value={keyType}
          onChange={(e) => setKeyType(e.target.value as KeyType)}
        >
          <FormControlLabel
            value="legacy"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2">Legacy / Webhook</Typography>
                <Typography variant="caption" color="text.secondary">Use for Zapier, CRM, or internal webhooks. No location or expiry.</Typography>
              </Box>
            }
          />
          <FormControlLabel
            value="client"
            control={<Radio />}
            label={
              <Box>
                <Typography variant="body2">Client (Core Contacts API)</Typography>
                <Typography variant="caption" color="text.secondary">Location-scoped key for GET /webhook/core-contacts. Supports one or multiple locations. Optional expiry.</Typography>
              </Box>
            }
          />
        </RadioGroup>
      </FormControl>

      {keyType === 'client' && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
          <BillingAccountSelector
            title="Locations"
            type="c"
            status="live"
            caller="create-api-key"
            multiple
            required
            width="100%"
            placeholder={selectedLocations.length === 0 ? 'Select one or more locations' : ''}
            value={selectedLocations}
            onSelected={setSelectedLocations}
          />
          <MuiTextField
            size="small"
            label="Expires (optional)"
            type="date"
            value={expiresAt}
            onChange={(e) => setExpiresAt(e.target.value)}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Box>
      )}

      {error && (
        <Alert severity="error" title={error.title} message={error.message} />
      )}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
        <Button label="Cancel" size="small" onClick={handleClose} />
        <Button label="Create key" size="small" onClick={handleCreateKey} loading={loading} />
      </Box>
    </>
  );

  const renderSuccessStep = () => (
    <>
      <Box sx={{ mb: 2 }}>
        <Alert
          severity="warning"
          title="Save your key now"
          message="This key is shown only once. Copy it and store it securely. You won't be able to see it again."
        />
      </Box>

      <FormLabel sx={{ mb: 0.5, display: 'block' }}>Your API key</FormLabel>
      <MuiTextField
        fullWidth
        size="small"
        value={keyRevealed ? createdKey : '•'.repeat(40)}
        variant="outlined"
        inputProps={{ readOnly: true }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => setKeyRevealed((v) => !v)}
                aria-label={keyRevealed ? 'Hide key' : 'Reveal key'}
              >
                {keyRevealed ? <VisibilityOff /> : <Visibility />}
              </IconButton>
              <IconButton
                size="small"
                onClick={handleCopyKey}
                aria-label="Copy key"
              >
                <ContentCopy />
              </IconButton>
            </InputAdornment>
          )
        }}
        sx={{ mb: 1 }}
      />
      {copied && (
        <Typography variant="caption" color="success.main" sx={{ display: 'block', mb: 1 }}>
          Copied to clipboard.
        </Typography>
      )}

      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
        Use in requests: <code style={{ background: '#f5f5f5', padding: '2px 6px', borderRadius: 4 }}>Authorization: Bearer YOUR_KEY</code>
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button label="Done" size="small" onClick={handleClose} />
      </Box>
    </>
  );

  return (
    <Modal
      open={open}
      onClose={handleClose}
      sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
    >
      <Paper sx={{ p: 3, width: '100%', maxWidth: 480, mx: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 2 }}>
          <TextView $fontSize={22}>
            {step === 'form' ? 'Create API key' : 'API key created'}
          </TextView>
          <IconButton size="small" onClick={handleClose} aria-label="Close">
            <Close />
          </IconButton>
        </Box>


        {step === 'form' && renderFormStep()}
        {step === 'success' && renderSuccessStep()}
      </Paper>
    </Modal>
  );
};

export default CreateKeyModal;
